<template>
  <section v-if="hasVisibleDashboardCard" class="relative grid grid-cols-1 lg:grid-cols-2 gap-2">
    <div
      v-if="dashboardLoading"
      class="absolute inset-0 z-20 rounded-2xl bg-white/90 backdrop-blur-[1px]"
      aria-busy="true"
      aria-live="polite"
    >
      <!-- sticky keeps the spinner visible in the viewport on tall mobile layouts -->
      <div class="sticky top-[min(40vh,12rem)] flex w-full items-center justify-center py-8 pointer-events-none">
        <div class="h-10 w-10 border-4 border-slate-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    </div>

    <div class=" mb-2 col-span-1 lg:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-2">
      <div
        v-for="card in todaySummaryCards"
        :key="card.title"
        class="h-[150px] rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden border-t-4 border-t-[#0d4169] p-4 md:p-5 flex flex-col min-w-0"
        :class="{ 'cursor-pointer hover:border-[#0d4169]/30 hover:bg-slate-50/80 transition-all': card.clickable && !dashboardLoading && !card.error }"
        :role="card.clickable ? 'button' : undefined"
        :tabindex="card.clickable && !dashboardLoading && !card.error ? 0 : -1"
        @click="onTodaySummaryCardClick(card)"
        @keydown.enter.prevent="onTodaySummaryCardClick(card)"
        @keydown.space.prevent="onTodaySummaryCardClick(card)"
      >
        <h2 class="text-base md:text-lg font-semibold text-slate-800 text-center shrink-0">
          {{ card.title }}
        </h2>
        <div v-if="card.error" class="flex-1 flex items-center justify-center min-h-0 px-2">
          <p class="text-sm text-rose-600 text-center">{{ card.error }}</p>
        </div>
        <div v-else class="flex-1 flex flex-col items-center justify-center min-h-0">
          <p class="text-3xl md:text-xl font-bold text-[#0d4169] tabular-nums">{{ card.value }}</p>
          <p v-if="card.clickable" class="mt-2 text-xs text-slate-500">Click to view report</p>
        </div>
      </div>
    </div>

    <div
      v-if="canSeeLowStockCard"
      class="h-[150px] rounded-2xl bg-rose-50/40 border border-slate-200 shadow-sm overflow-hidden border-t-4 border-t-orange-700 p-4 md:p-5 flex flex-col"
      :class="{ 'cursor-pointer hover:border-rose-200 hover:bg-rose-100/50 transition-all': !dashboardLoading && !errorMessage }"
      role="button"
      :tabindex="!dashboardLoading && !errorMessage ? 0 : -1"
      @click="onLowStockCardClick"
      @keydown.enter.prevent="onLowStockCardClick"
      @keydown.space.prevent="onLowStockCardClick"
    >
      <h2 class="text-base md:text-lg font-semibold text-slate-800 text-center shrink-0">
        Items Reached Min Stock Level
      </h2>

      <div v-if="errorMessage" class="text-sm text-rose-600 text-left flex-1 flex items-center">{{ errorMessage }}</div>

      <div v-else class="flex-1 flex flex-col items-center justify-center min-h-0">
        <p class="text-3xl md:text-xl font-bold text-rose-600 tabular-nums">{{ totalLowStockItemsCount }}</p>
        <p v-if="!totalLowStockItemsCount" class="mt-2 text-sm text-slate-400 italic text-center px-3">
          No items at or below min stock.
        </p>
        <p v-else class="mt-2 text-xs text-slate-500">Click to view details</p>
      </div>
    </div>
    <div v-if="canSeeExpiryCard" class="h-[150px] rounded-2xl bg-rose-50/40 border border-slate-200 shadow-sm overflow-hidden border-t-4 border-t-orange-700 p-4 md:p-5 flex flex-col">
      <div v-if="expiryErrorMessage" class="text-sm text-rose-600 text-left flex-1 flex items-center">{{ expiryErrorMessage }}</div>

      <div v-else class="grid grid-cols-2 gap-2 flex-1 min-h-0">
        <div
          class="flex flex-col items-center min-h-0 h-full rounded-lg border border-transparent px-2"
          :class="{ 'cursor-pointer hover:border-rose-200 hover:bg-rose-100/50 transition-all': !dashboardLoading && !expiryErrorMessage }"
          role="button"
          :tabindex="!dashboardLoading && !expiryErrorMessage ? 0 : -1"
          @click="onExpiredItemsClick"
          @keydown.enter.prevent="onExpiredItemsClick"
          @keydown.space.prevent="onExpiredItemsClick"
        >
          <h2 class="text-base md:text-lg font-semibold text-slate-800 text-center shrink-0 w-full">
            Expired Items
          </h2>
          <div class="flex-1 flex items-center justify-center min-h-0 w-full">
            <p class="text-3xl md:text-xl font-bold text-rose-600 tabular-nums">{{ totalExpiredExpiryItemsCount }}</p>
          </div>
        </div>

        <div
          class="flex flex-col items-center min-h-0 h-full rounded-lg border border-transparent px-2 border-l border-slate-200/60"
          :class="{ 'cursor-pointer hover:border-rose-200 hover:bg-rose-100/50 transition-all': !dashboardLoading && !expiryErrorMessage }"
          role="button"
          :tabindex="!dashboardLoading && !expiryErrorMessage ? 0 : -1"
          @click="onExpiringSoonItemsClick"
          @keydown.enter.prevent="onExpiringSoonItemsClick"
          @keydown.space.prevent="onExpiringSoonItemsClick"
        >
          <h2 class="text-base md:text-lg font-semibold text-slate-800 text-center shrink-0 w-full">
            Expiring Soon
          </h2>
          <div class="flex-1 flex items-center justify-center min-h-0 w-full">
            <p class="text-3xl md:text-xl font-bold text-rose-600 tabular-nums">{{ totalExpiringSoonExpiryItemsCount }}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="canSeeDailyOrderClassificationCard && 1==2" class="rounded-2xl bg-white border border-slate-200 shadow-sm p-4 md:p-5 h-[280px] flex flex-col overflow-hidden">
      <div class="flex items-center justify-between mb-3 shrink-0">
        <h2 class="text-base md:text-lg font-semibold text-slate-800 text-left">
          Daily Order Classification
        </h2>
        <span class="text-xs font-medium text-[#0d4169] bg-[#e9f2f8] px-2 py-1 rounded-full">Demo Data</span>
      </div>

      <div class="flex flex-col md:flex-row md:items-center gap-4 flex-1 min-h-0 overflow-hidden">
        <div class="mx-auto md:mx-0 relative w-36 h-36 shrink-0">
          <div
            class="w-36 h-36 rounded-full shadow-inner"
            :style="{ background: dailyCostPieGradient }"
          ></div>
          <div class="absolute inset-[22%] rounded-full bg-white border border-slate-100 flex items-center justify-center">
            <div class="text-center">
              <p class="text-[10px] text-slate-500 uppercase tracking-wider">Total</p>
              <p class="text-lg font-bold text-slate-800">{{ totalDailyOrders }}</p>
            </div>
          </div>
        </div>

        <div class="flex-1 grid gap-1.5 overflow-y-auto pr-1">
          <div
            v-for="segment in dailyOrderClassification"
            :key="segment.label"
            class="flex items-center justify-between rounded-lg border border-slate-50 px-3 py-1.5 bg-slate-50/30"
          >
            <div class="flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full" :style="{ backgroundColor: segment.color }"></span>
              <span class="text-xs font-medium text-slate-700">{{ segment.label }}</span>
            </div>
            <div class="text-right">
              <p class="text-xs font-semibold text-slate-800">{{ segment.value }}</p>
              <p class="text-[10px] text-slate-500">{{ segment.percent }}%</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="canSeeWeeklyGrossProfitCard" class="order-4 rounded-2xl bg-white border border-slate-200 shadow-sm p-4 md:p-5 h-[280px] flex flex-col overflow-hidden">
      <div class="flex items-center justify-between mb-3 shrink-0">
        <h2 class="text-base md:text-lg font-semibold text-slate-800 text-left">
          Weekly Total Sales
        </h2>
        <span class="text-[10px] font-medium text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full whitespace-nowrap">
          Last Sync: {{ lastSyncDisplay }}
        </span>
      </div>

      <div v-if="weeklySalesErrorMessage" class="flex-1 flex items-center justify-center text-rose-500 text-sm italic">
        {{ weeklySalesErrorMessage }}
      </div>
      <div v-else class="w-full flex-1 min-h-0 rounded-xl border border-slate-100 bg-slate-50/50 p-3 relative group/svg-sales">
        <svg viewBox="0 0 420 220" preserveAspectRatio="xMidYMid meet" class="w-full h-full">
          <defs>
            <linearGradient id="grossProfitGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#10b981" stop-opacity="0.35" />
              <stop offset="100%" stop-color="#10b981" stop-opacity="0.03" />
            </linearGradient>
          </defs>
          <line x1="45" y1="20" x2="45" y2="184" stroke="#cbd5e1" stroke-width="1" />
          <line x1="45" y1="184" x2="395" y2="184" stroke="#cbd5e1" stroke-width="1" />
          <line x1="45" y1="143" x2="395" y2="143" stroke="#e2e8f0" stroke-dasharray="4 4" stroke-width="1" />
          <line x1="45" y1="102" x2="395" y2="102" stroke="#e2e8f0" stroke-dasharray="4 4" stroke-width="1" />
          <line x1="45" y1="61" x2="395" y2="61" stroke="#e2e8f0" stroke-dasharray="4 4" stroke-width="1" />
          <line x1="45" y1="20" x2="395" y2="20" stroke="#e2e8f0" stroke-dasharray="4 4" stroke-width="1" />

          <path :d="grossProfitAreaPath" fill="url(#grossProfitGradient)" />
          <path :d="grossProfitLinePath" fill="none" stroke="#10b981" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />

          <g v-for="point in weeklyTotalSalesPoints" :key="`profit-${point.date}`">
            <circle 
              :cx="point.x" 
              :cy="point.y" 
              r="7" 
              fill="#10b981" 
              class="cursor-pointer"
              @mouseenter="hoveredSalesPoint = point"
              @mouseleave="hoveredSalesPoint = null"
            />
            <circle :cx="point.x" :cy="point.y" r="2.5" fill="#ffffff" pointer-events="none" />
            
            <text :x="point.x" :y="point.y - 12" text-anchor="middle" class="fill-slate-800 text-[12px] font-bold">
              {{ Number(point.totalPrice || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) }}
            </text>
            <text :x="point.x" y="208" text-anchor="middle" class="fill-slate-600 text-[12px] font-bold">
              {{ formatDateLabel(point.date) }}
            </text>
          </g>
        </svg>

        <!-- Tooltip for Sales Breakdown -->
        <div 
          v-if="hoveredSalesPoint && hoveredSalesPoint.deptBreakdown && hoveredSalesPoint.deptBreakdown.length"
          class="absolute z-10 bg-white border border-emerald-200 shadow-lg rounded-lg p-3 pointer-events-none text-[11px] w-max min-w-[140px]"
          :style="{ 
            left: `${(hoveredSalesPoint.x / 420) * 100}%`, 
            bottom: '60px',
            transform: hoveredSalesPoint.x > 310 ? 'translateX(-90%)' : (hoveredSalesPoint.x < 110 ? 'translateX(-10%)' : 'translateX(-50%)')
          }"
        >
          <p class="font-bold text-emerald-900 border-b border-emerald-50 pb-1 mb-1 whitespace-nowrap">{{ formatFullDate(hoveredSalesPoint.date) }}</p>
          <div v-for="dept in [...hoveredSalesPoint.deptBreakdown].sort((a,b) => a.name.localeCompare(b.name))" :key="dept.name" class="flex justify-between gap-6 py-1.5 border-b border-emerald-50/50 last:border-0 whitespace-nowrap">
            <span class="text-slate-600 font-medium">{{ dept.name }}:</span>
            <span class="font-bold text-slate-800">{{ Number(dept.amt || dept.totalPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) }}</span>
          </div>
          <div class="mt-1 pt-1 border-t border-emerald-100 flex justify-between gap-4">
            <span class="font-bold text-emerald-900">Total:</span>
            <span class="font-bold text-emerald-700">{{ Number(hoveredSalesPoint.totalPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="canSeeWeeklyTotalOrderSalesCard" class="order-3 rounded-2xl bg-white border border-slate-200 shadow-sm p-4 md:p-5 h-[280px] flex flex-col overflow-hidden">
      <div class="flex items-center justify-between mb-3 shrink-0">
        <h2 class="text-base md:text-lg font-semibold text-slate-800 text-left">
          Weekly Total Orders
        </h2>
        <span class="text-xs font-medium text-[#0d4169] bg-[#e9f2f8] px-2 py-1 rounded-full">Last 7 Days</span>
      </div>

      <div v-if="weeklySalesErrorMessage" class="flex-1 flex items-center justify-center text-rose-500 text-sm italic">
        {{ weeklySalesErrorMessage }}
      </div>
      <div v-else class="w-full flex-1 min-h-0 rounded-xl border border-slate-100 bg-slate-50/50 p-3 relative group/svg">
        <svg viewBox="0 0 420 220" preserveAspectRatio="xMidYMid meet" class="w-full h-full">
          <line x1="32" y1="20" x2="32" y2="184" stroke="#cbd5e1" stroke-width="1" />
          <line x1="32" y1="184" x2="395" y2="184" stroke="#cbd5e1" stroke-width="1" />
          <line x1="32" y1="143" x2="395" y2="143" stroke="#e2e8f0" stroke-dasharray="4 4" stroke-width="1" />
          <line x1="32" y1="102" x2="395" y2="102" stroke="#e2e8f0" stroke-dasharray="4 4" stroke-width="1" />
          <line x1="32" y1="61" x2="395" y2="61" stroke="#e2e8f0" stroke-dasharray="4 4" stroke-width="1" />
          <line x1="32" y1="20" x2="395" y2="20" stroke="#e2e8f0" stroke-dasharray="4 4" stroke-width="1" />

          <g v-for="point in weeklyOrderSales" :key="`bar-${point.date}`">
            <rect
              :x="point.x - weeklyBarWidth / 2"
              :y="184 - point.barHeight"
              :width="weeklyBarWidth"
              :height="point.barHeight"
              rx="6"
              fill="#0d4169"
              class="cursor-pointer transition-all duration-200 hover:fill-blue-800"
              @mouseenter="hoveredPoint = point"
              @mouseleave="hoveredPoint = null"
            />
            <text
              :x="point.x"
              :y="176 - point.barHeight"
              text-anchor="middle"
              class="fill-slate-800 text-[12px] font-bold"
            >
              {{ point.orders }}
            </text>
            <text
              :x="point.x"
              y="208"
              text-anchor="middle"
              class="fill-slate-600 text-[12px] font-bold"
            >
              {{ formatDateLabel(point.date) }}
            </text>
          </g>
        </svg>

        <!-- Tooltip for Department Breakdown -->
        <div 
          v-if="hoveredPoint && hoveredPoint.deptBreakdown && hoveredPoint.deptBreakdown.length"
          class="absolute z-10 bg-white border border-slate-200 shadow-lg rounded-lg p-3 pointer-events-none text-[11px] w-max min-w-[140px]"
          :style="{ 
            left: `${(hoveredPoint.x / 420) * 100}%`, 
            bottom: '60px',
            transform: hoveredPoint.x > 310 ? 'translateX(-90%)' : (hoveredPoint.x < 110 ? 'translateX(-10%)' : 'translateX(-50%)')
          }"
        >
          <p class="font-bold text-slate-800 border-b border-slate-100 pb-1 mb-1 whitespace-nowrap">{{ formatFullDate(hoveredPoint.date) }}</p>
          <div v-for="dept in [...hoveredPoint.deptBreakdown].sort((a,b) => a.name.localeCompare(b.name))" :key="dept.name" class="flex justify-between gap-6 py-1.5 border-b border-slate-50 last:border-0 whitespace-nowrap">
            <span class="text-slate-600 font-medium">{{ dept.name }}:</span>
            <span class="font-bold text-slate-800">{{ dept.quantity }}</span>
          </div>
          <div class="mt-1 pt-1 border-t border-slate-100 flex justify-between gap-4">
            <span class="font-bold text-slate-800">Total:</span>
            <span class="font-bold text-blue-700">{{ hoveredPoint.orders }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="canSeeDailyOrderClassificationCard && 1==2" class="order-5 rounded-2xl bg-white border border-slate-200 shadow-sm p-4 md:p-5 h-[280px] flex flex-col overflow-hidden">
      <div class="flex items-center justify-between mb-3 shrink-0">
        <h2 class="text-base md:text-lg font-semibold text-slate-800 text-left">
          Daily Cost
        </h2>
        <span class="text-xs font-medium text-fuchsia-700 bg-fuchsia-100 px-2 py-1 rounded-full">Daily Order Classification</span>
      </div>

      <div class="flex flex-col md:flex-row md:items-center gap-4 flex-1 min-h-0 overflow-hidden">
        <div
          class="mx-auto md:mx-0 w-40 h-40 rounded-full shadow-inner shrink-0"
          :style="{ background: dailyCostPieGradient }"
        ></div>

        <div class="flex-1 grid gap-1.5 overflow-y-auto pr-1">
          <div
            v-for="segment in dailyOrderClassification"
            :key="`cost-${segment.label}`"
            class="flex items-center justify-start gap-2 rounded-lg border border-slate-50 px-3 py-1.5 bg-slate-50/30"
          >
            <span class="w-2.5 h-2.5 rounded-full" :style="{ backgroundColor: segment.color }"></span>
            <span class="text-xs font-medium text-slate-700 truncate max-w-[140px]">{{ segment.label }}</span>
            <span class="text-xs font-semibold text-slate-800 whitespace-nowrap">{{ segment.percent }}%</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section v-else class="min-h-[320px] rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center p-8">
    <div class="text-center">
      <h1 class="text-2xl md:text-3xl font-bold text-slate-800">
        Welcome to Mina Software System
      </h1>
      <p class="mt-3 text-sm text-slate-500">
        Please select one component from the left side bar to continue
      </p>
    </div>
  </section>

  <!-- Low Stock Detail Modal -->
  <div
    v-if="lowStockModalOpen"
    class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
    @click.self="closeLowStockModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl shrink-0">
        <div class="text-left">
          <h3 class="text-base font-bold m-0">Items Reached Min Stock Level</h3>
          <p class="text-xs text-white/80 mt-0.5">{{ totalLowStockItemsCount }} item(s)</p>
        </div>
        <button
          type="button"
          class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
          @click="closeLowStockModal"
          aria-label="Close low stock modal"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="p-5 max-h-[70vh] overflow-y-auto">
        <div v-if="groupedLowStockItems.length" class="border border-slate-200 rounded-xl overflow-hidden">
          <table class="w-full border-collapse text-sm dashboard-table dashboard-table-auto">
            <thead>
              <tr class="bg-slate-50">
                <th class="px-3 py-2 text-left font-semibold text-slate-700 border-b">#</th>
                <th class="px-3 py-2 text-left font-semibold text-slate-700 border-b">Item</th>
                <th class="px-3 py-2 text-left font-semibold text-slate-700 border-b">Unit</th>
                <th class="px-3 py-2 text-center font-semibold text-slate-700 border-b">Min Stock</th>
                <th class="px-3 py-2 text-center font-semibold text-slate-700 border-b">Balance</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="group in groupedLowStockItems" :key="group.key">
                <tr v-for="(item, idx) in group.items" :key="item.id" class="border-b last:border-b-0">
                  <td class="text-left px-3 py-2">{{ idx + 1 }}</td>
                  <td class="text-left px-3 py-2 dashboard-item-name-cell">{{ item.itemName || '-' }}</td>
                  <td class="text-left px-3 py-2">{{ item.unit || '-' }}</td>
                  <td class="text-center px-3 py-2">{{ item.minStock }}</td>
                  <td class="text-center px-3 py-2 font-semibold text-rose-600">{{ item.current_balance }}</td>
                </tr>
                <tr class="border-b last:border-b-0">
                  <td colspan="5" class="px-3 py-2">
                    <div class="flex justify-end items-center gap-6 text-xs font-semibold text-slate-700">
                      <span>{{ group.name }}</span>
                      <span>{{ group.items.length }} items</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div v-else class="px-3 py-10 text-center text-slate-400 italic">
          No items at or below min stock.
        </div>
      </div>
    </div>
  </div>

  <!-- Expiry Detail Modal -->
  <div
    v-if="expiryModalType"
    class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
    @click.self="closeExpiryModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl shrink-0">
        <div class="text-left">
          <h3 class="text-base font-bold m-0">{{ expiryModalTitle }}</h3>
          <p class="text-xs text-white/80 mt-0.5">{{ expiryModalItemsCount }} item(s)</p>
        </div>
        <button
          type="button"
          class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
          @click="closeExpiryModal"
          aria-label="Close expiry modal"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="p-5 max-h-[70vh] overflow-y-auto">
        <div v-if="groupedExpiryModalItems.length" class="border border-slate-200 rounded-xl overflow-hidden">
          <table class="w-full border-collapse text-sm dashboard-table dashboard-table-auto">
            <thead>
              <tr class="bg-slate-50">
                <th class="px-3 py-2 text-left font-semibold text-slate-700 border-b">#</th>
                <th class="px-3 py-2 text-left font-semibold text-slate-700 border-b">Item</th>
                <th class="px-3 py-2 text-left font-semibold text-slate-700 border-b">Unit</th>
                <th class="px-3 py-2 text-left font-semibold text-slate-700 border-b">Unit Price</th>
                <th class="px-3 py-2 text-left font-semibold text-slate-700 border-b">Volume</th>
                <th class="px-3 py-2 text-left font-semibold text-slate-700 border-b">Expiry Date</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="group in groupedExpiryModalItems" :key="group.key">
                <tr
                  v-for="(item, idx) in group.items"
                  :key="`${item.source}-${item.id}-${item.unitPrice}-${item.volume}-${item.expiryDate}-${idx}`"
                  class="border-b last:border-b-0"
                >
                  <td class="px-3 text-left py-2">{{ idx + 1 }}</td>
                  <td class="px-3 text-left py-2 dashboard-item-name-cell">{{ item.itemName || '-' }}</td>
                  <td class="px-3 py-2">{{ item.unit || '-' }}</td>
                  <td class="px-3 py-2">{{ Number(item.unitPrice || 0).toFixed(2) }}</td>
                  <td class="px-3 py-2">{{ item.volume ?? '-' }}</td>
                  <td
                    class="px-3 py-2 font-semibold"
                    :class="isExpiredDate(item.expiryDate) ? 'text-rose-600' : 'text-slate-700'"
                  >
                    {{ item.expiryDate || '-' }}
                  </td>
                </tr>
                <tr class="border-b last:border-b-0">
                  <td colspan="6" class="px-3 py-2">
                    <div class="flex justify-end items-center gap-6 text-xs font-semibold text-slate-700">
                      <span>{{ group.name }}</span>
                      <span>{{ group.items.length }} items</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div v-else class="px-3 py-10 text-center text-slate-400 italic">
          {{ expiryModalEmptyMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import store from '../store'
import { apiFetch } from '../utils/api'

const router = useRouter()

const items = ref([])
const dashboardLoading = ref(false)
const errorMessage = ref('')
const expiryAlertItems = ref([])
const expiryErrorMessage = ref('')
const weeklyOrderSalesRaw = ref([])
const weeklySalesErrorMessage = ref('')
const lastPosOrderCreatedAt = ref(null)
const hoveredPoint = ref(null)
const hoveredSalesPoint = ref(null)
const lowStockModalOpen = ref(false)
const expiryModalType = ref(null)
const todayGrvTotal = ref(0)
const todayGrvError = ref('')
const todayNsTotal = ref(0)
const todayNsError = ref('')
const todaySivTotal = ref(0)
const todaySivError = ref('')

function formatTodaySummaryAmount(amount) {
  return Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const dailyOrderClassificationRaw = [
  { label: 'POS Orders', value: 120, color: '#2563eb' },
  { label: 'QR Orders', value: 80, color: '#f97316' },
  { label: 'Manual Orders', value: 45, color: '#10b981' }
]

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch?.id || null)
const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})

function hasAnyActionPrivilege(privObj) {
  if (!privObj) return false
  return !!(privObj.Add || privObj.Edit || privObj.View || privObj.Void || privObj.Approve)
}

function getPrivilegeBlock(name) {
  return userPrivileges.value?.[name] || userPrivileges.value?.[String(name).toLowerCase()] || null
}

function getLowStockCategoryInfo(item) {
  const rawCategory = item?.category

  if (rawCategory && typeof rawCategory === 'object') {
    const categoryName =
      rawCategory.name
      || rawCategory.categoryName
      || rawCategory.label
      || rawCategory.title
      || ''
    return {
      key: rawCategory.id ?? categoryName ?? item?.id,
      name: categoryName
    }
  }

  if (typeof rawCategory === 'string' && rawCategory.trim()) {
    return {
      key: rawCategory.trim(),
      name: rawCategory.trim()
    }
  }

  if (item?.categoryName) {
    return {
      key: item.categoryName,
      name: item.categoryName
    }
  }

  if (item?.category_name) {
    return {
      key: item.category_name,
      name: item.category_name
    }
  }

  if (item?.categoryLabel) {
    return {
      key: item.categoryLabel,
      name: item.categoryLabel
    }
  }

  return {
    key: item?.categoryId ?? item?.id,
    name: ''
  }
}

const hasAdminPrivilege = computed(() => (
  userPrivileges.value?.All === true || userPrivileges.value?.all === true
))
const canSeeLowStockCard = computed(() => (
  hasAdminPrivilege.value || hasAnyActionPrivilege(getPrivilegeBlock('Items Reached Min Stock Level'))
))
const canSeeExpiryCard = computed(() => (
  hasAdminPrivilege.value || hasAnyActionPrivilege(getPrivilegeBlock('Expired and Near Expiry Items'))
))
const canSeeDailyOrderClassificationCard = computed(() => (
  hasAdminPrivilege.value || hasAnyActionPrivilege(getPrivilegeBlock('Daily Order Classification'))
))
const canSeeWeeklyTotalOrderSalesCard = computed(() => (
  hasAdminPrivilege.value || hasAnyActionPrivilege(getPrivilegeBlock('Weekly Total Orders'))
))
const canSeeWeeklyGrossProfitCard = computed(() => (
  hasAdminPrivilege.value || hasAnyActionPrivilege(getPrivilegeBlock('Weekly Total Sales'))
))
const canSeeTodayGrvCard = computed(() => (
  hasAdminPrivilege.value || hasAnyActionPrivilege(getPrivilegeBlock("Today's Total GRV"))
))
const canSeeTodayNsCard = computed(() => (
  hasAdminPrivilege.value || hasAnyActionPrivilege(getPrivilegeBlock("Today's Total NS"))
))
const canSeeTodaySivCard = computed(() => (
  hasAdminPrivilege.value || hasAnyActionPrivilege(getPrivilegeBlock("Today's Total SIV"))
))

const todaySummaryCards = computed(() => ([
  ...(canSeeTodayGrvCard.value ? [{
    title: "Today's Total GRV",
    value: formatTodaySummaryAmount(todayGrvTotal.value),
    key: 'grv',
    clickable: true,
    error: todayGrvError.value
  }] : []),
  ...(canSeeTodayNsCard.value ? [{
    title: "Today's Total NS",
    value: formatTodaySummaryAmount(todayNsTotal.value),
    key: 'ns',
    clickable: true,
    error: todayNsError.value
  }] : []),
  ...(canSeeTodaySivCard.value ? [{
    title: "Today's Total SIV",
    value: formatTodaySummaryAmount(todaySivTotal.value),
    key: 'siv',
    clickable: true,
    error: todaySivError.value
  }] : []),
]))

function formatLastSyncDateTime(dateStr) {
  if (!dateStr) return '---'
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return '---'
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  const hh = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  const ss = String(date.getSeconds()).padStart(2, '0')
  return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`
}

const lastSyncDisplay = computed(() => formatLastSyncDateTime(lastPosOrderCreatedAt.value))

const hasVisibleDashboardCard = computed(() => (
  todaySummaryCards.value.length > 0
  || canSeeLowStockCard.value
  || canSeeExpiryCard.value
  || canSeeDailyOrderClassificationCard.value
  || canSeeWeeklyTotalOrderSalesCard.value
  || canSeeWeeklyGrossProfitCard.value
))

async function loadDashboardSummary() {
  const includes = []
  if (canSeeTodayGrvCard.value) includes.push('todayGrv')
  if (canSeeTodayNsCard.value) includes.push('todayNs')
  if (canSeeTodaySivCard.value) includes.push('todaySiv')
  if (canSeeLowStockCard.value) includes.push('lowStock')
  if (canSeeExpiryCard.value) includes.push('expiry')
  if (canSeeWeeklyTotalOrderSalesCard.value || canSeeWeeklyGrossProfitCard.value) {
    includes.push('weeklySales')
  }
  if (canSeeWeeklyGrossProfitCard.value) includes.push('lastSync')

  if (!includes.length) return

  if (!branchId.value && !companyId.value) {
    todayGrvError.value = 'Company or branch is required.'
    todayNsError.value = 'Company or branch is required.'
    todaySivError.value = 'Company or branch is required.'
    errorMessage.value = 'Company or branch is required.'
    expiryErrorMessage.value = 'Company or branch is required.'
    weeklySalesErrorMessage.value = 'Company or branch is required.'
    return
  }

  const params = new URLSearchParams()
  if (branchId.value) params.set('branchId', branchId.value)
  else if (companyId.value) params.set('companyId', companyId.value)
  params.set('include', includes.join(','))

  dashboardLoading.value = true
  errorMessage.value = ''
  expiryErrorMessage.value = ''
  weeklySalesErrorMessage.value = ''
  todayGrvError.value = ''
  todayNsError.value = ''
  todaySivError.value = ''

  try {
    const res = await apiFetch(`/dashboard/summary?${params.toString()}`)
    if (!res.ok) {
      throw new Error('Failed to load dashboard summary')
    }
    const data = await res.json().catch(() => ({}))
    const errors = data?.errors || {}

    if (includes.includes('todayGrv')) {
      todayGrvTotal.value = Number(data?.todayGrv?.totalPrice || 0)
      if (errors.todayGrv) todayGrvError.value = 'Unable to load GRV total.'
    }
    if (includes.includes('todayNs')) {
      todayNsTotal.value = Number(data?.todayNs?.totalPrice || 0)
      if (errors.todayNs) todayNsError.value = 'Unable to load NS total.'
    }
    if (includes.includes('todaySiv')) {
      todaySivTotal.value = Number(data?.todaySiv?.totalPrice || 0)
      if (errors.todaySiv) todaySivError.value = 'Unable to load SIV total.'
    }
    if (includes.includes('lowStock')) {
      items.value = Array.isArray(data?.lowStock) ? data.lowStock : []
      if (errors.lowStock) errorMessage.value = 'Unable to load low stock items.'
    }
    if (includes.includes('expiry')) {
      expiryAlertItems.value = Array.isArray(data?.expiry) ? data.expiry : []
      if (errors.expiry) expiryErrorMessage.value = 'Unable to load expiry alert items.'
    }
    if (includes.includes('weeklySales')) {
      weeklyOrderSalesRaw.value = Array.isArray(data?.weeklySales) ? data.weeklySales : []
      if (errors.weeklySales) weeklySalesErrorMessage.value = 'Failed to load weekly sales data'
    }
    if (includes.includes('lastSync')) {
      lastPosOrderCreatedAt.value = data?.lastSync?.createdAt || null
    }
  } catch (error) {
    console.error('Failed to load dashboard summary:', error)
    if (includes.includes('todayGrv')) todayGrvError.value = 'Unable to load GRV total.'
    if (includes.includes('todayNs')) todayNsError.value = 'Unable to load NS total.'
    if (includes.includes('todaySiv')) todaySivError.value = 'Unable to load SIV total.'
    if (includes.includes('lowStock')) errorMessage.value = 'Unable to load low stock items.'
    if (includes.includes('expiry')) expiryErrorMessage.value = 'Unable to load expiry alert items.'
    if (includes.includes('weeklySales')) weeklySalesErrorMessage.value = 'Error connecting to server'
    lastPosOrderCreatedAt.value = null
  } finally {
    dashboardLoading.value = false
  }
}

function onTodaySummaryCardClick(card) {
  if (!card?.clickable || dashboardLoading.value || card.error) return
  if (card.key === 'grv') {
    router.push({ name: 'report_grv' })
  } else if (card.key === 'ns') {
    router.push({ name: 'report_non_stockable' })
  } else if (card.key === 'siv') {
    router.push({ name: 'report_siv' })
  }
}

const lowStockItems = computed(() => {
  return (items.value || [])
    .filter((item) => {
      const minStock = Number(item.minStock ?? item.min_stock ?? 0)
      const current = Number(item.current_balance || 0)
      return Number.isFinite(minStock) && current <= minStock
    })
    .sort((a, b) => Number(a.current_balance || 0) - Number(b.current_balance || 0))
})

const groupedLowStockItems = computed(() => {
  const groups = new Map()

  lowStockItems.value.forEach((item) => {
    const categoryInfo = getLowStockCategoryInfo(item)
    if (!groups.has(categoryInfo.key)) {
      groups.set(categoryInfo.key, {
        key: categoryInfo.key,
        name: categoryInfo.name,
        items: []
      })
    }
    groups.get(categoryInfo.key).items.push(item)
  })

  return Array.from(groups.values()).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
})

const totalLowStockItemsCount = computed(() =>
  groupedLowStockItems.value.reduce((total, group) => total + group.items.length, 0)
)

function onLowStockCardClick() {
  if (dashboardLoading.value || errorMessage.value) return
  lowStockModalOpen.value = true
}

function closeLowStockModal() {
  lowStockModalOpen.value = false
}

function isExpiredDate(expiryDate) {
  if (!expiryDate) return false
  const d = new Date(expiryDate)
  if (Number.isNaN(d.getTime())) return false
  d.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return d <= today
}

const sortedExpiryAlertItems = computed(() => {
  return [...(expiryAlertItems.value || [])].sort((a, b) => {
    const aExpired = isExpiredDate(a.expiryDate)
    const bExpired = isExpiredDate(b.expiryDate)
    // If one is expired and the other is not, the expired one comes first (-1)
    if (aExpired !== bExpired) return aExpired ? -1 : 1
    // Within the same group, sort by expiry date (earliest first)
    return String(a.expiryDate || '').localeCompare(String(b.expiryDate || ''))
      || String(a.itemName || '').localeCompare(String(b.itemName || ''))
  })
})

function groupExpiryItemsByCategory(items) {
  const groups = new Map()

  items.forEach((item) => {
    const categoryInfo = getLowStockCategoryInfo(item)
    if (!groups.has(categoryInfo.key)) {
      groups.set(categoryInfo.key, {
        key: categoryInfo.key,
        name: categoryInfo.name,
        items: []
      })
    }
    groups.get(categoryInfo.key).items.push(item)
  })

  return Array.from(groups.values()).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
}

const expiredExpiryAlertItems = computed(() =>
  sortedExpiryAlertItems.value.filter((item) => isExpiredDate(item.expiryDate))
)

const expiringSoonExpiryAlertItems = computed(() =>
  sortedExpiryAlertItems.value.filter((item) => !isExpiredDate(item.expiryDate))
)

const groupedExpiredExpiryAlertItems = computed(() =>
  groupExpiryItemsByCategory(expiredExpiryAlertItems.value)
)

const groupedExpiringSoonExpiryAlertItems = computed(() =>
  groupExpiryItemsByCategory(expiringSoonExpiryAlertItems.value)
)

const totalExpiredExpiryItemsCount = computed(() => expiredExpiryAlertItems.value.length)

const totalExpiringSoonExpiryItemsCount = computed(() => expiringSoonExpiryAlertItems.value.length)

const groupedExpiryModalItems = computed(() => {
  if (expiryModalType.value === 'expired') return groupedExpiredExpiryAlertItems.value
  if (expiryModalType.value === 'soon') return groupedExpiringSoonExpiryAlertItems.value
  return []
})

const expiryModalTitle = computed(() => {
  if (expiryModalType.value === 'expired') return 'Expired Items'
  if (expiryModalType.value === 'soon') return 'Items expiring soon'
  return ''
})

const expiryModalItemsCount = computed(() => {
  if (expiryModalType.value === 'expired') return totalExpiredExpiryItemsCount.value
  if (expiryModalType.value === 'soon') return totalExpiringSoonExpiryItemsCount.value
  return 0
})

const expiryModalEmptyMessage = computed(() => {
  if (expiryModalType.value === 'expired') return 'No expired items found.'
  if (expiryModalType.value === 'soon') return 'No items expiring soon.'
  return 'No items found.'
})

function onExpiredItemsClick() {
  if (dashboardLoading.value || expiryErrorMessage.value) return
  expiryModalType.value = 'expired'
}

function onExpiringSoonItemsClick() {
  if (dashboardLoading.value || expiryErrorMessage.value) return
  expiryModalType.value = 'soon'
}

function closeExpiryModal() {
  expiryModalType.value = null
}

/** Parse YYYY-MM-DD as a local calendar date (same day semantics as POS report). */
function parseLocalDateKey(dateStr) {
  if (!dateStr) return null
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  }
  const d = new Date(dateStr)
  return Number.isNaN(d.getTime()) ? null : d
}

function formatDateLabel(dateStr) {
  const d = parseLocalDateKey(dateStr)
  if (!d) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yy = String(d.getFullYear()).slice(-2)
  return `${dd}/${mm}/${yy}`
}

function formatFullDate(dateStr) {
  const d = parseLocalDateKey(dateStr)
  if (!d) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

const weeklyMaxOrders = computed(() =>
  Math.max(...weeklyOrderSalesRaw.value.map((item) => item.orders || 0), 1)
)

const weeklyOrderSales = computed(() =>
  weeklyOrderSalesRaw.value.map((item, idx) => ({
    ...item,
    x: weeklySalesXStart.value + (idx * weeklySalesStep.value),
    barHeight: Math.max(0, Math.round((item.orders / weeklyMaxOrders.value) * 148))
  }))
)

const weeklySalesXStart = computed(() => 45)
const weeklySalesXEnd = computed(() => 390)
const weeklySalesStep = computed(() => {
  const total = weeklyOrderSalesRaw.value.length
  if (total <= 1) return 0
  return (weeklySalesXEnd.value - weeklySalesXStart.value) / (total - 1)
})
const weeklyBarWidth = computed(() => {
  if (weeklyOrderSalesRaw.value.length <= 1) return 26
  return Math.min(26, Math.max(14, Math.round(weeklySalesStep.value * 0.45)))
})

const weeklyMaxProfit = computed(() =>
  Math.max(...weeklyOrderSalesRaw.value.map((item) => item.totalPrice || 0), 1)
)

const weeklyTotalSalesPoints = computed(() => {
  return weeklyOrderSalesRaw.value.map((item, idx) => ({
    ...item,
    x: weeklySalesXStart.value + (idx * weeklySalesStep.value),
    y: 184 - Math.round(((item.totalPrice || 0) / weeklyMaxProfit.value) * 148)
  }))
})

const grossProfitLinePath = computed(() =>
  weeklyTotalSalesPoints.value
    .map((point, idx) => `${idx === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ')
)

const grossProfitAreaPath = computed(() => {
  if (!weeklyTotalSalesPoints.value.length) return ''
  const start = weeklyTotalSalesPoints.value[0]
  const end = weeklyTotalSalesPoints.value[weeklyTotalSalesPoints.value.length - 1]
  const line = weeklyTotalSalesPoints.value
    .map((point, idx) => `${idx === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ')
  return `${line} L ${end.x} 184 L ${start.x} 184 Z`
})

const totalDailyOrders = computed(() =>
  dailyOrderClassificationRaw.reduce((total, item) => total + item.value, 0)
)

const dailyOrderClassification = computed(() =>
  dailyOrderClassificationRaw.map((item) => ({
    ...item,
    percent: Math.round((item.value / totalDailyOrders.value) * 100)
  }))
)

const dailyCostPieGradient = computed(() => {
  let currentDeg = 0
  const total = totalDailyOrders.value || 1
  const segments = dailyOrderClassification.value
    .map((segment, idx, arr) => {
      const startDeg = currentDeg
      const isLast = idx === arr.length - 1
      const delta = isLast ? 360 - currentDeg : Math.round((segment.value / total) * 360)
      const endDeg = isLast ? 360 : currentDeg + delta
      currentDeg = endDeg
      return `${segment.color} ${startDeg}deg ${endDeg}deg`
    })
    .join(', ')

  return `conic-gradient(${segments})`
})

async function initDashboard() {
  await loadDashboardSummary()
}

onMounted(() => {
  initDashboard()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 11px;
  line-height: 1.15;
  color: #1e293b;
}

th, td {
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  padding: 3px 4px;
  font-size: inherit;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

.dashboard-table-wrap {
  overflow-x: hidden;
}

.dashboard-table th,
.dashboard-table td {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  vertical-align: top;
}

.dashboard-item-name-cell {
  line-height: 1.25;
}

.dashboard-table-auto {
  table-layout: auto;
}
</style>